import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import { RM_CHANNEL_ICON, RM_CHANNEL_LABEL } from '../../settings-reminders/types';
import type { RmChannel, RmSection, RmStep } from '../../settings-reminders/types';
import { audienceTag, stepSummary, whenLabel } from '../../settings-reminders/derive';
import type { RmIssue } from '../../settings-reminders/derive';

export interface RmStepsColumnProps {
  section: RmSection;
  selectedId: string | undefined;
  issues: RmIssue[];
  onSelect: (id: string) => void;
  onAdd: (channel: RmChannel) => void;
}

/**
 * The steps column (§3). Beside the editor rather than an accordion, for the same
 * reason as the recall builder: a full step editor is six sections, and inside an
 * accordion the shape of the sequence disappears exactly when you are changing it.
 *
 * A **check** and a **step** are the same thing with a different word — post-op
 * sections say *checks*, so the noun is a prop rather than two components.
 */
export function RmStepsColumn({ section, selectedId, issues, onSelect, onAdd }: RmStepsColumnProps) {
  const [adding, setAdding] = useState(false);
  const noun = section.kind === 'post_op' ? 'check' : 'step';
  const atMax = section.steps.length >= section.maxSteps;

  return (
    <div className="w-[392px] shrink-0 max-[1100px]:w-full">
      <div className="text-[10.5px] font-bold uppercase tracking-[0.09em] text-[var(--text-muted)]">
        The {noun}s
      </div>

      <div className="mt-2.5 flex flex-col gap-2">
        {section.steps.map((step, i) => (
          <StepCard
            key={step.id}
            step={step}
            section={section}
            index={i}
            selected={step.id === selectedId}
            issues={issues.filter((x) => x.stepId === step.id)}
            onSelect={() => {
              onSelect(step.id);
            }}
          />
        ))}
      </div>

      {/* Inline, not a popover: a popover over a column this narrow covers the
          list you are adding to. */}
      {section.maxSteps > 1 ? (
        adding ? (
          <div className="mt-2 rounded-[12px] border border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)] p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[12px] font-semibold text-[var(--text-primary)]">Add a {noun} on which channel?</span>
              <button
                type="button"
                aria-label="Close"
                onClick={() => {
                  setAdding(false);
                }}
                className="text-[var(--text-muted)]"
              >
                <Icon kind="x" size={13} stroke={2.2} />
              </button>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(['text', 'email', 'ai_call'] as RmChannel[]).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => {
                    onAdd(c);
                    setAdding(false);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-[9px] border border-[var(--border-default)] bg-[var(--surface-card)] px-2.5 py-1.5 text-[12px] font-semibold text-[var(--text-secondary)] hover:border-[var(--brand-indigo)]"
                >
                  <Icon kind={RM_CHANNEL_ICON[c] as IconKind} size={12} stroke={2} />
                  {RM_CHANNEL_LABEL[c]}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <button
            type="button"
            disabled={atMax}
            onClick={() => {
              setAdding(true);
            }}
            title={atMax ? `At most ${String(section.maxSteps)} ${noun}s` : undefined}
            className={cn(
              'mt-2 w-full rounded-[12px] border border-dashed px-3 py-2.5 text-[12.5px] font-semibold',
              atMax
                ? 'cursor-not-allowed border-[var(--border-default)] text-[var(--text-muted)]'
                : 'border-[var(--brand-indigo)] text-[var(--brand-indigo)] hover:bg-[var(--brand-indigo-tint)]',
            )}
          >
            + Add a {noun}
          </button>
        )
      ) : null}

      {/* The promise under the column. For *before*, the one that matters is that
          a moved appointment stops the old sequence dead. */}
      <p className="m-0 mt-3 text-[11.5px] leading-[1.6] text-[var(--text-muted)]">
        {section.kind === 'post_op' ? (
          <>
            Only after a completed visit.{' '}
            <b className="font-semibold text-[var(--text-secondary)]">
              A cancelled or missed {section.title.toLowerCase()} appointment gets no checks.
            </b>
          </>
        ) : (
          <>
            Every step checks the appointment when it sends.{' '}
            <b className="font-semibold text-[var(--text-secondary)]">
              Moved or cancelled appointments get nothing more from the old time.
            </b>
          </>
        )}
      </p>
    </div>
  );
}

function StepCard({
  step,
  section,
  index,
  selected,
  issues,
  onSelect,
}: {
  step: RmStep;
  section: RmSection;
  index: number;
  selected: boolean;
  issues: RmIssue[];
  onSelect: () => void;
}) {
  const tag = audienceTag(step);
  return (
    <div
      onClick={onSelect}
      className={cn(
        'cursor-pointer rounded-[12px] border p-3',
        selected
          ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)]'
          : 'border-[var(--border-default)] bg-[var(--surface-card)] hover:border-[#D6DBEA]',
        // Off is dimmed, not hidden — a step that exists and is switched off is
        // information, and hiding it makes it look deleted.
        !step.on && 'opacity-60',
      )}
    >
      <div className="flex items-start gap-2.5">
        <span
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px]',
            selected ? 'bg-[var(--brand-indigo)] text-white' : 'bg-[#F1F3F9] text-[var(--text-muted)]',
          )}
        >
          <Icon kind={RM_CHANNEL_ICON[step.channel] as IconKind} size={13} stroke={2} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-[12.5px] font-bold text-[var(--text-primary)]">
              {RM_CHANNEL_LABEL[step.channel]}
            </span>
            <span className="font-[var(--font-mono)] text-[11px] text-[var(--text-muted)]">
              {whenLabel(step, section.kind)}
            </span>
            {tag === undefined ? null : (
              <span
                className={cn(
                  'rounded-[5px] px-1.5 py-[1px] text-[9.5px] font-bold uppercase tracking-[0.06em]',
                  tag === '2 versions'
                    ? 'bg-[var(--brand-indigo)] text-white'
                    : 'bg-[#F1F3F9] text-[var(--text-muted)]',
                )}
              >
                {tag}
              </span>
            )}
            {step.on ? null : (
              <span className="rounded-[5px] bg-[#F1F3F9] px-1.5 py-[1px] text-[9.5px] font-bold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                Off
              </span>
            )}
            <span className="sr-only">Step {index + 1}</span>
          </span>
          <span className="mt-1 block truncate text-[11.5px] text-[var(--text-secondary)]">{stepSummary(step)}</span>
          {issues.length > 0 ? (
            <span className="mt-1 flex items-center gap-1.5 text-[11px] font-semibold text-[var(--alert-red)]">
              <span className="h-[6px] w-[6px] rounded-full bg-[var(--alert-red)]" />
              {issues[0]?.short}
              {issues.length > 1 ? ` +${String(issues.length - 1)}` : ''}
            </span>
          ) : null}
        </span>
      </div>
    </div>
  );
}
